/**
 * Team Switcher component for sidebar.
 *
 * Displays the current team and allows switching between teams the user belongs to.
 * Owners see all teams with member counts. Regular users see only their teams.
 * Includes "Create Team" action for owners/admins.
 */

import type { TeamWithRole } from '@/api/teams'
import { teamsApi } from '@/api/teams'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useSwitchTeam, useTeams } from '@/hooks/use-team'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'
import { useTeamStore } from '@/store/team'
import { Check, ChevronsUpDown, Crown, Loader2, Plus, Settings, Shield, Users } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

// Role badge colors
const roleBadgeColors: Record<string, string> = {
  owner: 'text-yellow-600 dark:text-yellow-400',
  admin: 'text-purple-600 dark:text-purple-400',
  manager: 'text-blue-600 dark:text-blue-400',
  member: 'text-green-600 dark:text-green-400',
  viewer: 'text-gray-600 dark:text-gray-400',
}

function getTeamInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function TeamSwitcher() {
  const currentTeam = useTeamStore((s) => s.currentTeam)
  const currentTeamId = useTeamStore((s) => s.currentTeamId)
  const currentRole = useTeamStore((s) => s.currentRole)
  const teams = useTeamStore((s) => s.teams)
  const setTeams = useTeamStore((s) => s.setTeams)
  const userAvatarUrl = useAuthStore((s) => s.user?.avatarUrl)
  const switchTeam = useSwitchTeam()
  const { refetch: refetchTeams } = useTeams()

  const navigate = useNavigate()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  const handleSwitchTeam = async (team: TeamWithRole) => {
    if (team.id === currentTeamId) return
    try {
      await switchTeam.mutateAsync(team.id)
      toast.success(`Switched to ${team.name}`)
    } catch {
      toast.error('Failed to switch team')
    }
  }

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return
    setIsCreating(true)
    try {
      const newTeam = await teamsApi.createTeam({ name: newTeamName.trim() })
      toast.success(`Team "${newTeam.name}" created`)
      setNewTeamName('')
      setCreateDialogOpen(false)
      // Refresh teams list in both React Query cache and Zustand store
      const { data: freshTeams } = await refetchTeams()
      if (freshTeams) setTeams(freshTeams)
      await switchTeam.mutateAsync(newTeam.id)
    } catch {
      toast.error('Failed to create team')
    } finally {
      setIsCreating(false)
    }
  }

  // Any user can create a team (they become owner of the new team)
  const canCreateTeam = true

  const createTeamDialog = (
    <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Team</DialogTitle>
          <DialogDescription>
            Create a team to collaborate with your colleagues. You can invite members after creation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Team Name</Label>
            <Input
              placeholder="e.g. Marketing Team"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateTeam()}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreateTeam} disabled={!newTeamName.trim() || isCreating}>
            {isCreating ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            Create Team
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  // No teams — show prompt to create first team
  if (teams.length === 0 && !currentTeam) {
    return (
      <>
        <Button
          variant="outline"
          className="w-full justify-start gap-2 px-3 py-2 h-auto"
          onClick={() => setCreateDialogOpen(true)}
        >
          <Plus className="h-5 w-5 shrink-0 text-muted-foreground" />
          <span className="text-sm">Create Your First Team</span>
        </Button>
        {createTeamDialog}
      </>
    )
  }

  // Loading state: teams not yet loaded
  if (!currentTeam) {
    return (
      <div className="flex items-center gap-2 px-3 py-2">
        <Skeleton className="h-7 w-7 rounded-full shrink-0" />
        <div className="flex-1 min-w-0 space-y-1">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-2.5 w-14" />
        </div>
      </div>
    )
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="w-full justify-between px-3 py-2 h-auto"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Avatar className="h-7 w-7 shrink-0">
                <AvatarImage src={userAvatarUrl} />
                <AvatarFallback className="text-xs bg-primary/10 text-primary">
                  {getTeamInitials(currentTeam.name)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 text-left">
                <div className="text-sm font-medium truncate">{currentTeam.name}</div>
                <div className={cn('text-[10px] capitalize', roleBadgeColors[currentRole || 'member'])}>
                  {currentRole}
                </div>
              </div>
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-64" align="start" sideOffset={8}>
          <DropdownMenuLabel className="flex items-center gap-2 text-xs text-muted-foreground">
            <Users className="h-3.5 w-3.5" />
            Your Teams
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {teams.map((team) => {
            const isActive = team.id === currentTeamId
            const RoleIcon = team.myRole === 'owner' ? Crown : team.myRole === 'admin' ? Shield : Users

            return (
              <DropdownMenuItem
                key={team.id}
                onClick={() => handleSwitchTeam(team)}
                className={cn('flex items-center gap-2 py-2 cursor-pointer', isActive && 'bg-accent')}
              >
                <Avatar className="h-6 w-6 shrink-0">
                  {isActive && userAvatarUrl && <AvatarImage src={userAvatarUrl} />}
                  <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                    {getTeamInitials(team.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{team.name}</div>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <RoleIcon className={cn('h-3 w-3', roleBadgeColors[team.myRole])} />
                    <span className="capitalize">{team.myRole}</span>
                    <span>·</span>
                    <span>{team.memberCount} {team.memberCount === 1 ? 'member' : 'members'}</span>
                  </div>
                </div>
                {isActive && <Check className="h-4 w-4 shrink-0 text-primary" />}
              </DropdownMenuItem>
            )
          })}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => navigate('/settings?tab=team')}
            className="flex items-center gap-2 cursor-pointer"
          >
            <Settings className="h-4 w-4" />
            <span>Manage Team</span>
          </DropdownMenuItem>

          {canCreateTeam && (
            <DropdownMenuItem
              onClick={() => setCreateDialogOpen(true)}
              className="flex items-center gap-2 cursor-pointer"
            >
              <Plus className="h-4 w-4" />
              <span>Create Team</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {createTeamDialog}
    </>
  )
}
